/**
 * Consolidated conversation types
 * This is the single source of truth for conversation-related types
 */

/**
 * Conversation status types that match the backend API
 */
export type ConversationStatus = 
  | 'active'
  | 'needs_review'
  | 'archived'
  | 'completed'
  | 'ai_responding' // Temporary status, cannot be set via select

/**
 * Platform/channel types
 */
export type Platform = 
  | 'whatsapp' 
  | 'instagram' 
  | 'facebook' 
  | 'web' 
  | 'phone_call' 
  | 'wordpress' 
  | 'twitter' 
  | 'betterplace'
  | 'email'
  | 'shopify'
  | 'other'

/** Nested `client` on conversation list/detail API responses. */
export interface ConversationClient {
  id: string
  name?: string | null
  email?: string | null
  phone?: string | null
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  user_category?: string
  profile_pic_url?: string | null
}

/**
 * Conversation interface as returned by the API
 */
export interface Conversation {
  id: string
  user: string
  client: ConversationClient | unknown
  user_display_name: string
  title: string
  description?: string
  started_at: string
  source: string
  platform: string
  feedback?: string
  channel_user_id?: string
  channel_conversation_id?: string
  status: ConversationStatus
  metadata?: Record<string, unknown>
  timestamp: string
  message_count: number
  unread_count?: number
  can_reply?: boolean
  reply_block_reason?: string | null
  last_message?: {
    text: string
    sender: string
    timestamp: string
  }
  user_tags?: Array<{
    id?: string
    name?: string
    color?: string
    description?: string
    tag_type?: string
  }>
  /** De que trata esta conversacion. `user_tags` es la persona, y no es lo mismo. */
  conversation_tags?: Array<{
    id?: string
    name?: string
    color?: string
    description?: string
    tag_type?: string
  }>
  lead_source?: string
}

/**
 * Conversation list response from API
 */
export interface ConversationsListResponse {
  results?: Conversation[]
  conversations?: Record<string, Conversation[]>
  total_count?: number
  count?: number
  lead_source_filter?: string
  source_filter?: string
  status_filter?: string
  category_filter?: string
  tags_filter?: string
  tag_ids_filter?: string
}

/**
 * Conversation item for UI display (replaces "Mail" interface)
 * This is the transformed version used in components
 */
export interface ConversationItem {
  id: string
  userId: string
  displayName: string
  email?: string
  phone?: string
  title: string
  lastMessage: string
  timestamp: string
  displayDate: string
  status: ConversationStatus
  platform: Platform
  isAnalyzing?: boolean
  category?: string
  tags?: string[]
  feedback?: string
  unreadCount?: number
}

/**
 * Message interface
 */
export type AttachmentType = 'image' | 'audio' | 'video' | 'document' | 'sticker' | 'location' | 'contact'

export interface MessageAttachment {
  id: string
  type: AttachmentType
  attachment_type?: AttachmentType
  mime_type: string
  filename: string
  file_url: string | null
  transcription: string
  ai_description: string
  /** Trace from rag-api enrich + stored lengths (optional). */
  enrichment_meta?: Record<string, unknown>
  enrichment_status?: string
  enrichment_error_code?: string
  enrichment_error_message?: string
  enrichment_completed_at?: string | null
  caption: string
  latitude: number | null
  longitude: number | null
  location_name: string
  location_address: string
  created_at?: string
}

export interface Message {
  id: string
  conversation: string
  text: string
  sender: 'user' | 'assistant' | 'admin' | 'bot'
  status?: string
  timestamp: string
  metadata?: Record<string, unknown>
  attachments?: MessageAttachment[]
}

/**
 * Display names for conversation statuses
 * @deprecated Use getStatusDisplayName function with i18n instead
 */
export const CONVERSATION_STATUS_DISPLAY_NAMES: Record<ConversationStatus, string> = {
  active: 'Active',
  needs_review: 'Needs Review',
  archived: 'Archived',
  completed: 'Completed',
  ai_responding: 'AI Responding'
}

/**
 * Get display name for conversation status with i18n support
 */
export function getStatusDisplayName(status: ConversationStatus, t: (key: string) => string): string {
  const statusKey = `functions.conversation_status.${status}`
  const translated = t(statusKey)
  
  // Si la traducción devuelve la misma clave, usar el fallback
  if (translated === statusKey) {
    return CONVERSATION_STATUS_DISPLAY_NAMES[status] || status
  }
  
  return translated
}

/**
 * Get CSS classes for conversation status styling
 */
export function getStatusStyles(status: ConversationStatus): string {
  const baseStyles = "text-xs font-medium"
  
  switch (status) {
    case 'active':
      return `${baseStyles} bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400`
    case 'needs_review':
      return `${baseStyles} bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400`
    case 'archived':
      return `${baseStyles} bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400`
    case 'completed':
      return `${baseStyles} bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400`
    case 'ai_responding':
      return `${baseStyles} bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 animate-pulse`
    default:
      return `${baseStyles} bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400`
  }
}
